import { configureAudio, getAudioSettings } from './audio.js';

export const musicTracks = [
  {
    id: 'dark-place',
    name: 'Dark Place',
    artist: 'SkyleTheFrench',
    license: 'CC0',
    url: 'assets/audio/music/dark_place.mp3',
    mood: 'ambient',
  },
  {
    id: 'technological-messup',
    name: 'Technological Messup',
    artist: 'Centurion_of_war',
    license: 'CC0',
    url: 'assets/audio/music/technological_messup.mp3',
    mood: 'combat',
  },
  {
    id: 'fast-techno-action',
    name: 'Fast Techno Action Loop',
    artist: 'annandistance',
    license: 'CC0',
    url: 'assets/audio/music/fast_techno_action_loop.mp3',
    mood: 'boss',
  },
];

let music = null;

export function initMusic(audioCtx, musicBus) {
  if (!audioCtx || !musicBus) return music;
  if (music) return music;
  const gain = audioCtx.createGain();
  const filter = audioCtx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 5200;
  gain.gain.value = 0.0001;
  filter.connect(gain);
  gain.connect(musicBus);
  music = {
    ctx: audioCtx,
    bus: musicBus,
    filter,
    gain,
    current: null,
    nodes: new Map(),
    trackIndex: Number(localStorage.getItem('dead-loop-music-track') || 0) || 0,
    threat: 0,
    targetGain: 0.14,
    stingerCooldown: 0,
  };
  loadMusicTracks();
  return music;
}

export function updateMusic(threatLevel = 0, dt = 1 / 60, options = {}) {
  if (!music) return;
  music.threat = Math.max(0, Math.min(1, threatLevel));
  const enabled = getAudioSettings().musicEnabled;
  const intensity = options.bossActive ? 1 : Math.max(0.34, music.threat);
  music.targetGain = enabled ? 0.075 + intensity * 0.105 : 0.0001;
  const rate = 1 - Math.exp(-dt * 3.8);
  music.gain.gain.value += (music.targetGain - music.gain.gain.value) * rate;
  music.filter.frequency.value += ((options.bossActive ? 9800 : 3600 + music.threat * 5200) - music.filter.frequency.value) * rate;
  music.stingerCooldown = Math.max(0, music.stingerCooldown - dt);
  if (enabled && !music.current) startSelectedTrack();
}

export function cycleMusicTrack(direction = 1) {
  if (!music) return null;
  music.trackIndex = (music.trackIndex + Math.sign(direction || 1) + musicTracks.length) % musicTracks.length;
  localStorage.setItem('dead-loop-music-track', String(music.trackIndex));
  startSelectedTrack();
  dispatchMusicEvent();
  return getMusicState();
}

export function toggleMusicEnabled(force = null) {
  const next = force ?? !getAudioSettings().musicEnabled;
  configureAudio({ musicEnabled: next });
  if (!next) stopTrack();
  else startSelectedTrack();
  dispatchMusicEvent();
  return next;
}

export function getMusicState() {
  const track = musicTracks[music?.trackIndex || 0] || musicTracks[0];
  return {
    enabled: getAudioSettings().musicEnabled,
    track,
    loaded: !!music?.nodes.get(track.id),
  };
}

export async function loadMusicTracks() {
  if (!music || music.nodes.size) return music;
  for (const track of musicTracks) {
    try {
      const audio = new Audio(track.url);
      audio.loop = true;
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      audio.volume = 1;
      const source = music.ctx.createMediaElementSource(audio);
      const trackGain = music.ctx.createGain();
      trackGain.gain.value = 1;
      source.connect(trackGain);
      trackGain.connect(music.filter);
      music.nodes.set(track.id, { audio, source, gain: trackGain });
    } catch (error) {
      console.warn('[music] failed to prepare track', track.id, error);
    }
  }
  startSelectedTrack();
  dispatchMusicEvent();
  return music;
}

export function triggerStinger(type = 'combat') {
  if (!music || music.stingerCooldown > 0 || !getAudioSettings().musicEnabled) return;
  const ctx = music.ctx;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  const freq = type === 'boss' ? 62 : type === 'death' ? 46 : 330;
  osc.type = type === 'death' ? 'triangle' : 'square';
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(type === 'boss' ? 31 : freq * 0.5, ctx.currentTime + 0.38);
  filter.type = 'lowpass';
  filter.frequency.value = type === 'combat' ? 1200 : 720;
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(type === 'boss' ? 0.08 : 0.045, ctx.currentTime + 0.025);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.52);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(music.bus);
  osc.start();
  osc.stop(ctx.currentTime + 0.6);
  music.stingerCooldown = 1.2;
}

function startSelectedTrack() {
  if (!music || !getAudioSettings().musicEnabled) return;
  const track = musicTracks[music.trackIndex] || musicTracks[0];
  const node = music.nodes.get(track.id);
  if (!node) return;
  stopTrack();
  node.audio.currentTime = 0;
  node.audio.play().catch((error) => console.warn('[music] play blocked', error));
  music.current = node;
}

function stopTrack() {
  if (!music?.current) return;
  music.current.audio.pause();
  music.current = null;
}

function dispatchMusicEvent() {
  window.dispatchEvent(new CustomEvent('dead-loop:music-change', { detail: getMusicState() }));
}
